import { ArrowUpRight, BookOpen, Pill, Syringe, FlaskConical, ScanLine, FileText, QrCode, Scale, Bone, GitBranch, Dog, ShieldCheck, Stethoscope } from "lucide-react";

export const VETCURAPET_URL = "https://vetcura.info/mit-dyr/opret";
export const VETCURAPET_LOGIN_URL = "https://vetcura.info/mit-dyr/login";

const FEATURES = [
  [Scale, "Vægt & vækst", "Følg vægtkurver og udvikling over tid."],
  [BookOpen, "Medicinsk journal", "Alle besøg, diagnoser og noter samlet."],
  [Pill, "Medicin", "Aktuel medicin, doser og fornyelse af recept."],
  [Syringe, "Vaccinationer", "Status, historik og påmindelser."],
  [FlaskConical, "Prøvesvar", "Blodprøver og laboratoriesvar."],
  [Bone, "Muskuloskeletal", "Led, knogler og bevægeapparat."],
  [ScanLine, "Røntgen & billeder", "Røntgen og scanninger til visning."],
  [FileText, "Dokumenter", "Attester og journaler – del med QR-kode."],
  [GitBranch, "Stamtavle", "Slægt og racedata."],
  [QrCode, "Digitalt dyrepas", "Dyrepas med QR-kode til deling."],
  [Dog, "Raceviden", "Typiske træk, temperament, pleje og sundhedsforhold."],
];
const STEPS = [
  ["Opret din gratis profil", "Tilmeld dig med din e-mail. Ingen skjulte gebyrer."],
  ["Tilknyt dine kæledyr", "Tilføj hund, kat eller andre kæledyr med chipnummer eller dyrlægeregistrering."],
  ["Modtag dyrlægedata automatisk", "Journalnotater, vacciner, prøvesvar og røntgen fra tilsluttede klinikker."],
  ["Del kæledyrspas via QR", "Vis vaccinationer og sundhedsstatus hos pension, træner eller på rejsen."],
];
const SHOTS = [["app-start", "Kom godt i gang"], ["app-oversigt", "Din oversigt"], ["app-profil", "Dit dyrs profil"]];

export const PetCta = ({ testid = "vetcurapet-cta", label = "Opret gratis profil" }) => (
  <a href={VETCURAPET_URL} target="_blank" rel="noopener" className="btn-primary" data-testid={testid}>{label}<ArrowUpRight className="h-4 w-4" /></a>
);

export const PetPromo = ({ testid = "vetcurapet-promo", stacked = false }) => (
  <div className={`card-vp p-5 flex flex-col gap-4 bg-[#E6F4EF] border-[#D2E5E1] ${stacked ? "items-start" : "sm:flex-row sm:items-center"}`} data-testid={testid}>
    <img src="/images/vetcura-logo.png" alt="VetCura" className="h-10 w-10 rounded-lg bg-white object-contain" width="40" height="40" />
    <div className="flex-1">
      <p className="font-heading font-bold text-[#153C3A]">Saml dit dyrs journal og medicin – gratis</p>
      <p className="text-sm text-[#52706D]">Med VetCura Pet har du journal, medicin, vacciner og dyrepas samlet ét sted.</p>
    </div>
    <a href="/mit-dyr" className="btn-outline btn-sm" data-testid={`${testid}-link`}>Læs mere</a>
  </div>
);

export default function VetCuraPet() {
  return (
    <div data-testid="vetcurapet-page">
      <section className="container-vp py-14 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="eyebrow">VetCura Pet · til hunde- og katteejere</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#153C3A] mt-3">Alt om dit dyr – ét sted, altid opdateret</h1>
          <p className="text-base md:text-lg text-[#52706D] mt-5 max-w-xl">Se vægt, journal, medicin, vacciner, prøvesvar, røntgen, dokumenter, stamtavle og digitalt dyrepas i én app – samlet fra din dyreklinik.</p>
          <div className="flex flex-wrap gap-3 mt-7"><PetCta testid="vetcurapet-signup" /><a href={VETCURAPET_LOGIN_URL} target="_blank" rel="noopener" className="btn-outline" data-testid="vetcurapet-login">Log ind</a></div>
          <p className="text-xs text-[#52706D] mt-4 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#087F72]" />Gratis at bruge · GDPR-sikret · drives af VetCura</p>
        </div>
        <img src="/images/vetcurapet-app-oversigt.webp" alt="VetCura Pet: oversigt over dine kæledyr" className="w-full max-w-md mx-auto rounded-2xl shadow-lg" width="700" height="766" fetchpriority="high" />
      </section>

      <section className="bg-white border-y border-[#E6F0EE]"><div className="container-vp py-14">
        <h2 className="text-2xl font-bold text-[#153C3A]">Det kan du se og dele om dit dyr</h2>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="vetcurapet-features">
          {FEATURES.map(([Icon, t, d]) => <li key={t} className="card-vp p-4"><Icon className="h-5 w-5 text-[#087F72]" /><p className="font-semibold text-[#153C3A] mt-2">{t}</p><p className="text-sm text-[#52706D]">{d}</p></li>)}
        </ul>
      </div></section>

      <section className="container-vp py-14">
        <h2 className="text-2xl font-bold text-[#153C3A]">Sådan virker det</h2>
        <ol className="grid md:grid-cols-4 gap-4 mt-6" data-testid="vetcurapet-steps">
          {STEPS.map(([t, d], i) => <li key={t} className="card-vp p-5"><span className="font-heading text-3xl font-extrabold text-[#087F72]">0{i + 1}</span><p className="font-semibold text-[#153C3A] mt-2">{t}</p><p className="text-sm text-[#52706D] mt-1">{d}</p></li>)}
        </ol>
        <div className="grid sm:grid-cols-3 gap-6 mt-12">
          {SHOTS.map(([f, t]) => <figure key={f}><img src={`/images/vetcurapet-${f}.webp`} alt={`VetCura Pet: ${t}`} className="w-full rounded-xl shadow" width="700" height="766" loading="lazy" /><figcaption className="text-sm font-semibold text-center text-[#153C3A] mt-2">{t}</figcaption></figure>)}
        </div>
      </section>

      <section className="bg-white border-y border-[#E6F0EE]"><div className="container-vp py-14 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#153C3A]">Dyrepasset med QR-kode</h2>
          <p className="text-[#52706D] mt-3">Det digitale dyrepas samler vaccinationer, chip-ID og dokumenter med en QR-kode, du kan dele med pension, ny klinik eller grænsekontrol.</p>
          <h3 className="font-bold text-[#153C3A] mt-8 flex items-center gap-2"><Stethoscope className="h-5 w-5 text-[#087F72]" />Er din dyrlæge ikke med i VetCura endnu?</h3>
          <p className="text-sm text-[#52706D] mt-2">Via VetCuras gæstedyrlæge-side kan en dyrlæge indtaste journaler, vaccinationer og prøvesvar på dit dyr uden at være oprettet i systemet.</p>
        </div>
        <img src="/images/vetcurapet-app-pas.webp" alt="Digitalt kæledyrspas i VetCura Pet" className="w-full max-w-md mx-auto rounded-2xl shadow" width="700" height="730" loading="lazy" />
      </div></section>

      <section className="container-vp py-14 text-center">
        <h2 className="text-2xl font-bold text-[#153C3A]">Klar til at samle dit dyrs sundhed?</h2>
        <p className="text-[#52706D] mt-2">Gratis for alle dyreejere i Danmark.</p>
        <div className="mt-6 flex justify-center"><PetCta testid="vetcurapet-signup-bottom" label="Opret din gratis profil" /></div>
        <p className="text-xs text-[#52706D] mt-6">VetCura Pet drives af VetCura (vetcura.info). VetPris gemmer ingen journal- eller medicindata.</p>
      </section>
    </div>
  );
}
